import { useEffect, useMemo, useRef, useState } from "react";

import { isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";

import {
	assignLettersToRuleGroups,
	createExpressionAndAddToParentQuestion,
	createRulesExpression,
	getInitialRules,
} from "./SimplifiedBuilder/Components/ExtraSettings/RulesTab/utils";
import { getFormElementsFromFormConfig } from "./SimplifiedBuilder/Internal/FormConverter";

export const useRules = ({ parentQuestionConfig, completeFormModel, onChange, ruleType }) => {
	const prevRuleType = useRef(ruleType);
	const prevParentQuestionValue = useRef(parentQuestionConfig.value);

	const [ showRuleSelector, setShowRuleSelector ] = useState(false);
	const [ selectedGroup, setSelectedGroup ] = useState(0);
	const [ groups, setGroups ] = useState(() => getInitialRules(parentQuestionConfig, completeFormModel, ruleType));

	const resetGroups = () => {
		setGroups(getInitialRules(parentQuestionConfig, completeFormModel, ruleType));
	};

	useEffect(() => {
		if (prevRuleType.current !== ruleType || prevParentQuestionValue.current !== parentQuestionConfig.value) {
			prevRuleType.current = ruleType;
			prevParentQuestionValue.current = parentQuestionConfig.value;
			resetGroups();
		}
	}, [ completeFormModel, parentQuestionConfig.value, ruleType ]);

	const save = groups => {
		setGroups(groups);
		const newExpression = createExpressionAndAddToParentQuestion(groups, ruleType);
		onChange({ [ruleType]: newExpression });
	};

	const addRuleToGroup = question => {
		const tempGroupsList = groups.length > 0 ? [ ...groups ] : [ [] ];
		tempGroupsList[selectedGroup].push(question);
		const nextRules = assignLettersToRuleGroups(tempGroupsList);
		save(nextRules);
		setShowRuleSelector(false);
	};

	const removeRuleFromGroup = (groupIndex, questionIndex) => {
		const tempGroupsList = [ ...groups ];
		tempGroupsList[groupIndex].splice(questionIndex, 1);
		if (tempGroupsList[groupIndex].length === 0) {
			tempGroupsList.splice(groupIndex, 1);
		}

		const nextRules = assignLettersToRuleGroups(tempGroupsList);
		save(nextRules);
		setShowRuleSelector(false);
	};

	const addGroup = () => {
		const newGroups = [ ...groups, [] ];
		setSelectedGroup(newGroups.length - 1);
		save(newGroups);
	};

	const openModalAssignGroupNumber = groupNumber => {
		setSelectedGroup(groupNumber);
		setShowRuleSelector(true);
	};

	const ruleModalProps = useMemo(() => {
		if (isNonEmptyObject(completeFormModel))  {
			const rules = getFormElementsFromFormConfig(completeFormModel);
			const sections = [ ...new Set(rules.map(item => item.section)) ];

			return { rules, sections };
		}

		return { rules: [], sections: [] };
	}, [ completeFormModel ]);

	const openRuleSelectorModal = index => {
		setSelectedGroup(index);
		setShowRuleSelector(true);
	};

	const ruleExpression = createRulesExpression(groups);

	return {
		resetGroups,
		groups,
		showRuleSelector,
		setShowRuleSelector,
		ruleExpression,
		addRuleToGroup,
		removeRuleFromGroup,
		addGroup,
		openModalAssignGroupNumber,
		ruleModalProps,
		openRuleSelectorModal,
	};
};
